import React, { memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { BackButton } from 'src/components/shared/BackButton'
import { Button } from 'src/components/shared/Button'
import { ContentCard } from 'src/components/shared/ContentCard'
import { GSIcon } from 'src/components/shared/GSIcon'

interface Props {
  title: string
  subtitle?: string
  /** 1-based position in the queue. 0 when the item is not in the loaded queue. */
  position: number
  total: number
  onBack(): void
  /** Opens the previous row in the queue. Disabled on the first row. */
  onPrevious(): void
  /** Opens the next row in the queue. Disabled on the last row. */
  onNext(): void
}

const ReviewHeaderComponent = ({
  title,
  subtitle,
  position,
  total,
  onBack,
  onPrevious,
  onNext,
}: Props) => {
  return (
    <ContentCard className={tw`flex flex-row items-center gap-4 px-6 py-4`}>
      <BackButton label="Back to queue" onClick={onBack} dataTestId="document-review-back-button" />
      <div css={tw`min-w-0`}>
        <Typography variant="h3" css={tw`truncate text-shades-900`}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" css={tw`truncate text-shades-500`}>
            {subtitle}
          </Typography>
        ) : null}
      </div>
      {/* Hidden rather than shown as "0 of 0": a deep link opened without the queue loaded has
          no position to report, and inventing one would misstate how much work is left. */}
      {position > 0 && total > 0 ? (
        <div css={tw`ml-auto flex shrink-0 flex-row items-center gap-2`}>
          <Button
            size="small"
            color="white"
            iconOnly
            disabled={position <= 1}
            leftIcon={
              <GSIcon iconName="gs_chevron_left_small" size={20} css={tw`text-shades-1000`} />
            }
            onClick={onPrevious}
            aria-label="Previous in queue"
            data-testid="document-review-previous-in-queue"
          />
          <Typography variant="body2" css={tw`text-shades-500`}>
            {position} of {total}
          </Typography>
          <Button
            size="small"
            color="white"
            iconOnly
            disabled={position >= total}
            leftIcon={
              <GSIcon iconName="gs_chevron_right_small" size={20} css={tw`text-shades-1000`} />
            }
            onClick={onNext}
            aria-label="Next in queue"
            data-testid="document-review-next-in-queue"
          />
        </div>
      ) : null}
    </ContentCard>
  )
}

export const ReviewHeader = memo(ReviewHeaderComponent)
